'use client';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BusinessDetailsStep from './BusinessDetailsStep';
import ContactDetailsStep from './ContactDetailsStep';
import ExperienceStep from './ExperienceStep';
import ProgressStep from './ProgressStep';
import IndustryStep from './IndustryStep';
import RegisteredAgentStep from './RegisteredAgentStep';
import PricingStep from './PricingStep';
import Button from 'components/generic-components/thera-start-components/Button';
import Quote from './Quote';
import CheckoutAccordionStep from './CheckoutAccordionStep';
import WorryFreeComplianceStep from './WorryFreeComplianceStep';
import EINDocumentsStep from './EINDocumentsStep';
import OperatingAgreementStep from './OperatingAgreementStep';
import FasterFilingStep from './FasterFilingStep';

// Define the validation schemas
const stepSchemas = [
  Yup.object({
    businessName: Yup.string()
      .matches(
        /^[a-zA-Z0-9 .,'&()-]+$/,
        'Business name can only contain letters, numbers, spaces, and basic punctuation',
      )
      .required('Business name is required'),
    state: Yup.string().required('State is required').notOneOf([''], 'State is required'),
  }),
  Yup.object({
    firstName: Yup.string()
      .matches(/^[a-zA-Z ]+$/, 'First name can only contain letters and spaces')
      .required('First name is required'),
    lastName: Yup.string()
      .matches(/^[a-zA-Z ]+$/, 'Last name can only contain letters and spaces')
      .required('Last name is required'),
    userPersonalAddress: Yup.object({
      line1: Yup.string()
        .matches(/^[a-zA-Z0-9\s,'-./#&()]+$/, 'Address can only contain letters, numbers, and basic punctuation')
        .required('Address Line 1 is required'),
      line2: Yup.string().matches(
        /^[a-zA-Z0-9\s,'-./#&()]*$/,
        'Address can only contain letters, numbers, and basic punctuation',
      ),
      city: Yup.string()
        .matches(/^[a-zA-Z\s,'-.()]+$/, 'City can only contain letters and basic punctuation')
        .required('City is required'),
      state: Yup.string().when('country', {
        is: (country) => ['US', 'IN', 'AU', 'BR', 'MX', 'NZ', 'NG', 'CA'].includes(country),
        then: (schema) => schema.required('State is required'),
        otherwise: (schema) => schema,
      }),
      postalCode: Yup.string()
        .matches(/^[a-zA-Z0-9\s-]+$/, 'Postal code can only contain letters, numbers, spaces, and dashes')
        .required('Postal code is required'),
      country: Yup.string().required('Country is required'),
    }).required('Address is required'),
    email: Yup.string()
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Invalid email format')
      .email('Invalid email')
      .required('Email is required'),
    phone: Yup.string()
      .matches(/^[0-9+()\-\s]+$/, 'Phone can only contain numbers, spaces, parentheses, dashes, and plus sign')
      .required('Phone is required'),
  }),
  Yup.object({
    experience: Yup.string().required('Please select your business experience'),
  }),
  Yup.object({
    progress: Yup.string().required('Please select your business progress'),
  }),
  Yup.object({
    industry: Yup.string().required('Please select your industry'),
  }),
  Yup.object({
    agent: Yup.string().required('Please select a registered agent option'),
  }),
  Yup.object({}), // Pricing step: no required fields
  Yup.object({}), // Compliance step: no required fields
  Yup.object({}), // EIN step: no required fields
  Yup.object({}),
  Yup.object({}),
];

// Initial values for the form
const initialValues = {
  businessName: '',
  state: '',
  firstName: '',
  lastName: '',
  userPersonalAddress: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
  },
  email: '',
  phone: '',
  experience: '',
  progress: '',
  industry: '',
  agent: '',
  package: '',
  fasterFiling: false,
  worryFreeCompliance: false,
  einDocuments: '',
  operatingAgreement: false,
};

export default function MultiStepForm() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [step]);

  // Formik setup with dynamic validation schema
  const formik = useFormik({
    initialValues,
    validationSchema: stepSchemas[step],
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (step < 9) {
        setStep(step + 1);
      } else {
        // Final submit
        console.info('All form data:', values);
      }
    },
    enableReinitialize: false,
  });

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // Step rendering logic
  const renderStep = () => {
    switch (step) {
      case 0:
        return <BusinessDetailsStep formik={formik} />;
      case 1:
        return <ContactDetailsStep formik={formik} onBack={handleBack} />;
      case 2:
        return <ExperienceStep formik={formik} onBack={handleBack} />;
      case 3:
        return <ProgressStep formik={formik} onBack={handleBack} />;
      case 4:
        return <IndustryStep formik={formik} onBack={handleBack} />;
      case 5:
        return <RegisteredAgentStep formik={formik} onBack={handleBack} onNext={() => setStep(6)} />;
      default:
        return null;
    }
  };

  return (
    <>
      {step <= 5 && (
        <div className="flex w-full flex-1 items-center justify-center px-4 py-8 sm:px-8 md:w-[537px]">
          <form
            onSubmit={formik.handleSubmit}
            className="w-full"
            style={{ boxShadow: '0px 4px 32px 0px rgba(16, 24, 40, 0.08)' }}
          >
            <div
              className={`flex w-full flex-col gap-0 ${step <= 5 ? 'rounded-2xl bg-white p-6 shadow-xl sm:p-8 md:p-10' : 'text-white'}`}
            >
              {renderStep()}

              {step !== 5 && (
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    className="mt-2 w-full rounded-lg !bg-[#181818] py-2 text-[1rem] font-[500] text-white shadow-none"
                  >
                    {step === stepSchemas.length - 1 ? 'Submit' : 'Continue'}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      {step <= 5 && <Quote />}

      {step > 5 && step !== 11 && (
        <div className="flex w-full max-w-[765px] flex-col px-4 py-8 sm:px-8">
          {step === 6 && <PricingStep formik={formik} onBack={handleBack} setStep={setStep} />}
          {step === 7 && <WorryFreeComplianceStep formik={formik} onBack={handleBack} onNext={() => setStep(8)} />}

          {step === 8 && <EINDocumentsStep formik={formik} onBack={handleBack} onNext={() => setStep(9)} />}
          {/* this component needs to be renamed to Trade Mark company */}
          {step === 9 && <OperatingAgreementStep formik={formik} onBack={handleBack} onNext={() => setStep(10)} />}
          {/* this component needs to be renamed to DBA Service step */}
          {step === 10 && <FasterFilingStep formik={formik} onBack={handleBack} onNext={() => setStep(11)} />}
        </div>
      )}

      {step === 11 && (
        <div className="flex w-full max-w-[645px] flex-col px-4 py-8 sm:px-8">
          <CheckoutAccordionStep formik={formik} onBack={handleBack} />
        </div>
      )}
    </>
  );
}
